export interface VersionHistoryEntry {
  hash: string;
  timestamp: string; // ISO timestamp
  fullVersion: string;
  environment: string;
}

const VERSION_HISTORY_KEY = 'wordpop-version-history';
const MAX_HISTORY_ITEMS = 10;

export function getVersionHistory(): VersionHistoryEntry[] {
  try {
    const stored = localStorage.getItem(VERSION_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to parse version history:', error);
    return [];
  }
}

export function addVersionToHistory(entry: VersionHistoryEntry): void {
  try {
    const history = getVersionHistory();
    
    // Check if this build hash already exists
    const existingIndex = history.findIndex(h => h.hash === entry.hash);
    if (existingIndex !== -1) {
      // Update timestamp if it already exists
      history[existingIndex].timestamp = entry.timestamp;
    } else {
      // Add new entry at the beginning
      history.unshift(entry);
      
      // Keep only last 10 items
      if (history.length > MAX_HISTORY_ITEMS) {
        history.splice(MAX_HISTORY_ITEMS);
      }
    }
    
    localStorage.setItem(VERSION_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Failed to save version history:', error);
  }
}

export function clearVersionHistory(): void {
  try {
    localStorage.removeItem(VERSION_HISTORY_KEY);
  } catch (error) {
    console.error('Failed to clear version history:', error);
  }
}

export function formatRelativeTime(isoTimestamp: string): string {
  const now = new Date();
  const then = new Date(isoTimestamp);
  const diffMs = now.getTime() - then.getTime();
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) !== 1 ? 's' : ''} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) !== 1 ? 's' : ''} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) !== 1 ? 's' : ''} ago`;
}
